import DOMPurify from 'dompurify'

/**
 * Client-side sanitizing for work-item descriptions.
 *
 * The backend sanitizes on WRITE and is the real security boundary — see the API's
 * `modules/cards/richText.ts`. This is the second layer, and it exists because "the server
 * says it is clean" is a claim, not a guarantee: a stale row written before the sanitizer
 * landed, a bad migration, a future endpoint that forgets, or a compromised response all end
 * up as a string we are about to hand to `dangerouslySetInnerHTML`. Nothing reaches the DOM
 * unsanitized, even when the server insists it already is.
 *
 * The allow-list below is the SAME list the backend enforces. If one side changes, the other
 * has to change with it — a tag the editor emits but this list drops disappears from the
 * reader's screen while still sitting in the database, which is the confusing failure.
 */

/** p, br, inline marks, lists, h2/h3, quotes, code and links. Nothing else survives. */
export const RICH_TEXT_ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'h2',
  'h3',
  'blockquote',
  'code',
  'pre',
  'a',
] as const

/**
 * No `style`, no `class`, no `id`, and no `on*` handlers — an allow-list is the only way to
 * be certain none slipped through.
 */
const ALLOWED_ATTR = ['href', 'title', 'target', 'rel']

/**
 * http, https and mailto only.
 *
 * `javascript:` in an href executes on click, which is the classic route from "stored text"
 * to "script execution". DOMPurify's default expression also permits relative URLs; the
 * backend does not, so this is narrowed to match rather than left on the default.
 */
const ALLOWED_URI_REGEXP = /^(?:https?|mailto):/i

/**
 * Every surviving link opens in a new tab with `rel="noopener noreferrer"`.
 *
 * The backend already stamps both, so this only matters for markup that predates it — but
 * `target="_blank"` without `rel` hands `window.opener` to the destination, and adding it
 * here costs nothing. The hook is registered once, on the module's own DOMPurify singleton,
 * and this module is the only caller of `sanitize` in the app.
 */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

/** Clean markup, ready to inject. The ONLY input `dangerouslySetInnerHTML` may ever get. */
export function sanitizeRichText(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...RICH_TEXT_ALLOWED_TAGS],
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    // Anything not on the tag list loses its markup but keeps its words, so a pasted styled
    // document degrades to readable text instead of silently losing content.
    KEEP_CONTENT: true,
  })
}

/** The words, with every tag removed. Used to answer "does this render as anything?". */
export function richTextToPlainText(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [], KEEP_CONTENT: true })
}

/**
 * Is there anything left once the markup is stripped?
 *
 * An editor that has been cleared still emits `<p></p>` — a non-empty string that passes any
 * length check while showing the reader nothing at all. The backend rejects exactly this, so
 * the form asks the same question before submitting and the user gets an inline message
 * instead of a round trip and a toast.
 */
export function hasVisibleText(html: string): boolean {
  return richTextToPlainText(html).trim().length > 0
}

const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

/**
 * Plain text as paragraphs, for seeding the editor from a `descriptionFormat: 'text'` row.
 *
 * The escaping is the point. TipTap parses whatever string it is handed as HTML, so feeding
 * it a legacy description containing `a < b` unescaped would either swallow the rest of the
 * text or invent an element. Blank lines become paragraph breaks and single newlines become
 * `<br>`, which is what `whitespace-pre-wrap` was showing the reader before the upgrade.
 */
export function plainTextToHtml(text: string): string {
  const escaped = text.replace(/[&<>"']/g, (c) => ESCAPES[c]!)
  const paragraphs = escaped.split(/\n{2,}/)
  return paragraphs
    .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
    .join('')
}
